import type {Query} from "../../cdx-models/utils/MakeModel";
import type createCache from "./cache";
import type {ModelDesc, ModelDescriptions, ModelName} from "./mate-types";

const isDev = process.env.NODE_ENV !== "production";

type QueryRegistry = Pick<
  ReturnType<typeof createCache>,
  "addConstraintBasedRelationField" | "addConstraintBasedRelationRelation"
>;

/** The relation keys each cache has registered; registering a key again adds nothing. */
const registeredKeys = new WeakMap<QueryRegistry, Set<string>>();

type RegisterQueryInCacheParams = {
  parentModel: ModelName;
  relName: string;
  query: Query<any>;
  targetModelDesc: ModelDesc;
  cache: QueryRegistry;
  descriptions: ModelDescriptions;
  /** the query is on the relation's own rows, not on rows it reaches through a relation */
  isOwnRow: boolean;
};
const registerQueryInCache = (params: RegisterQueryInCacheParams) => {
  const {parentModel, relName, query, targetModelDesc, descriptions, cache, isOwnRow} = params;
  // account.$meta.find("cards", {effort: 2})
  // targetModelDesc: "card"
  // parentModel: "account" + relName
  const addField = (field: string) =>
    cache.addConstraintBasedRelationField({
      parentModel,
      relName,
      targetModel: targetModelDesc.name,
      field,
      isOwnRow,
    });

  for (const [rawKey, val] of Object.entries(query)) {
    if (rawKey[0] === "$") {
      if (rawKey === "$or" || rawKey === "$and") {
        for (const q of val as Query<any>[]) registerQueryInCache({...params, query: q});
      } else if (rawKey === "$order") {
        const fields: string[] = Array.isArray(val) ? val : [val];
        for (const field of fields) addField(field[0] === "-" ? field.slice(1) : field);
      }
      continue;
    }
    const key = rawKey[0] === "!" ? rawKey.slice(1) : rawKey;
    const hasManyDesc = targetModelDesc.hasMany[key];
    if (
      targetModelDesc.fields[key] ||
      targetModelDesc.fkToBelongsTo[key] ||
      (hasManyDesc && hasManyDesc.fkAsArray)
    ) {
      addField(key);
      continue;
    }
    if (hasManyDesc) {
      cache.addConstraintBasedRelationRelation({
        parentModel,
        relName,
        targetModel: hasManyDesc.model,
        fk: hasManyDesc.fk!,
      });
      // `{decks: {…}}` holds the relation's `filter` too, so a field it reads changes the result
      const relQuery = hasManyDesc.filter ? {$and: [val, hasManyDesc.filter]} : val;
      registerQueryInCache({
        ...params,
        query: relQuery,
        targetModelDesc: descriptions[hasManyDesc.model],
        isOwnRow: false,
      });
      continue;
    }
    const belongsToDesc = targetModelDesc.belongsTo[key];
    if (belongsToDesc) {
      addField(belongsToDesc.fk);
      registerQueryInCache({
        ...params,
        query: val,
        targetModelDesc: descriptions[belongsToDesc.model],
        isOwnRow: false,
      });
    }
  }
};

type CreateQueryStringParams = {
  modelDesc: ModelDesc;
  query: Query<any>;
  relName: string;
  prefix?: string;
  descriptions: ModelDescriptions;
  cache: QueryRegistry;
};

/**
 * creates stable string representation for better caching,
 * also analyses query dependencies to be able invalidate query
 * if dependencies are updated
 */
export const processQuery = ({
  modelDesc,
  query,
  relName,
  prefix = "",
  descriptions,
  cache,
}: CreateQueryStringParams) => {
  const hasManyDesc = modelDesc.hasMany[relName];
  if (isDev) {
    if (!hasManyDesc) throw new Error(`Unknown rel '${relName}' for '${modelDesc.name}'`);
  }

  const queryObj = Object.fromEntries(
    Object.keys(query)
      .sort()
      .map((key) => {
        const entry = (query as any)[key];
        if (entry && entry.$meta)
          throw new Error(
            "Does not support passing instance as constraint anymore, please use its id instead!"
          );
        return [key, entry];
      })
  );

  const fullRelName = `${prefix}${relName}(${JSON.stringify(queryObj)})`;
  if ((hasManyDesc as any).model !== "$deprecated") {
    let registered = registeredKeys.get(cache);
    if (!registered) registeredKeys.set(cache, (registered = new Set()));
    const registryKey = `${modelDesc.name}.${fullRelName}`;
    if (!registered.has(registryKey)) {
      registerQueryInCache({
        parentModel: modelDesc.name,
        relName: fullRelName,
        query,
        targetModelDesc: descriptions[hasManyDesc.model],
        descriptions,
        cache,
        isOwnRow: true,
      });
      registered.add(registryKey);
    }
  }

  return {fullRelName};
};
